/**
 * Markdown Format Constants - Single Source of Truth
 *
 * Shared directive markers for the prompt editor's markdown format.
 * Used by:
 * - markdown-serializer.ts (producer) - TipTap JSON → Markdown
 * - markdown-parser.ts (consumer) - Markdown → TipTap JSON
 * - paste-handler.ts (detection) - Paste detection
 *
 * IMPORTANT: Changing these values affects round-trip conversion.
 * Always test: serialize → parse → serialize should produce identical output.
 */

// =============================================================================
// Directive Markers
// =============================================================================

/**
 * Section directive markers
 * Format: ::: section: name="Title", deploymentSpecific=false, ... :::
 */
export const SECTION_DIRECTIVE = {
  PREFIX: "::: section:",
  SUFFIX: ":::"
} as const;

/**
 * Tab directive markers
 * Format: ::: tab: DEPLOYMENT_TYPE :::
 */
export const TAB_DIRECTIVE = {
  PREFIX: "::: tab:",
  SUFFIX: ":::"
} as const;

// =============================================================================
// Builder Functions (for serializer)
// =============================================================================

/**
 * Builds a complete section directive line
 * @param metaContent - The metadata content (e.g., 'name="Title", deploymentSpecific=false')
 * @example buildSectionDirective('name="My Section", deploymentSpecific=false')
 * // Returns: '::: section: name="My Section", deploymentSpecific=false :::'
 */
export function buildSectionDirective(metaContent: string): string {
  return `${SECTION_DIRECTIVE.PREFIX} ${metaContent} ${SECTION_DIRECTIVE.SUFFIX}`;
}

/**
 * Builds a complete tab directive line
 * @param deploymentType - The deployment type (e.g., 'NEXUS', 'WHATSAPP')
 * @example buildTabDirective('SLACK')
 * // Returns: '::: tab: SLACK :::'
 */
export function buildTabDirective(deploymentType: string): string {
  return `${TAB_DIRECTIVE.PREFIX} ${deploymentType} ${TAB_DIRECTIVE.SUFFIX}`;
}

// =============================================================================
// Detection Functions (for paste-handler)
// =============================================================================

/**
 * Checks if text contains structured markdown (section or tab directives)
 * Used for paste detection to determine if content should be parsed as markdown
 */
export function containsStructuredMarkdown(text: string): boolean {
  return text.includes(SECTION_DIRECTIVE.PREFIX) || text.includes(TAB_DIRECTIVE.PREFIX);
}

/**
 * Checks if a line starts a new section directive
 * Used by parser to split markdown into sections
 */
export function isSectionDirectiveStart(line: string): boolean {
  return line.trim().startsWith(SECTION_DIRECTIVE.PREFIX);
}

// =============================================================================
// Mention Patterns - Single Source of Truth
// =============================================================================

/**
 * Regex patterns for mention detection.
 * Used by both parser PATTERNS and marked tokenizers.
 *
 * Pattern structure:
 * - BASE: The core pattern without global flag (for tokenizers with ^ anchor)
 * - GLOBAL: The global version (for search/replace operations)
 */

// Variable mentions: {{variableName}}
export const VARIABLE_MENTION_PATTERN = /\{\{([^}]+)\}\}/;
export const VARIABLE_MENTION_PATTERN_GLOBAL = /\{\{([^}]+)\}\}/g;

// Skill mentions: @skillName (lowercase letters, digits, underscores — no hyphens)
export const SKILL_MENTION_PATTERN = /@([a-z0-9](?:_?[a-z0-9])*)/;
export const SKILL_MENTION_PATTERN_GLOBAL = /@([a-z0-9](?:_?[a-z0-9])*)/g;

/**
 * Whether a mention trigger may open when `charBefore` sits in front of it.
 *
 * `SKILL_MENTION_PATTERN` is anchored at the tokenizer's cursor and so cannot
 * express its own left boundary — the `@` inside `yann.lauwers@icloud.com` is
 * indistinguishable from one starting a mention once the cursor sits on it. An
 * `@` bound to the word in front of it belongs to that word; anything else — a
 * space, a newline, punctuation, the start of the input, or a non-text node
 * that yields an empty string — opens a mention.
 *
 * Word character means any Unicode letter or number, plus `_`. ASCII alone would
 * read `é` and `张` as boundaries and split `andré@corp.com` exactly the way this
 * rule exists to prevent — and `_` is a word character to `SKILL_MENTION_PATTERN`,
 * so excluding it would reopen the same split one character further along.
 *
 * Anchored, because this is an exported contract and the parameter is one
 * character: an unanchored class would accept `"→a"` as bound, matching the `a`
 * a caller never meant to offer.
 *
 * This is the single definition of that boundary, and both consumers offer it
 * the same thing: the character a reader SEES before the trigger. marked's
 * tokenizer reaches it via {@link precedingCharFromTokens}, which descends its
 * token tree to the last rendered character rather than reading the markup; the
 * TipTap suggestion matcher reads it straight out of the document. Agreeing on
 * the argument is as load-bearing as sharing this predicate — offering the two
 * different characters would let one text resolve one way as it is typed and
 * another as it is loaded. The name is trigger-neutral because that matcher
 * fronts variable and collection mentions as well as skills.
 */
export function isMentionTriggerBoundary(charBefore: string): boolean {
  return !/^[\p{L}\p{N}_]$/u.test(charBefore);
}

/**
 * The last whole code point of `text`, or the empty string when it has none.
 *
 * A code point, not a code unit. `.slice(-1)` alone returns the lone low
 * surrogate of an astral character — which `\p{L}` correctly reports is not a
 * letter, so {@link isMentionTriggerBoundary} would admit and an astral
 * character before the trigger would open a mention. Only the last two units
 * are spread, so this read stays O(1) on a long string rather than walking it.
 *
 * Shared, because both consumers of that boundary have to offer it the same
 * character: two hand-rolled code-point reads is how that agreement stops
 * holding without anything failing.
 */
export function lastCodePoint(text: string): string {
  const codePoints = [...text.slice(-2)];
  return codePoints[codePoints.length - 1] ?? "";
}

/**
 * One of marked's inline tokens, reduced to the three fields the boundary reads.
 *
 * `raw` is the source the token consumed; `text` is what a reader actually sees
 * (`**x**` renders as `x`); `tokens` are its children, present on every token
 * that nests — emphasis, links, and the rest.
 */
export interface InlineTokenLeftContext {
  readonly raw?: string;
  readonly text?: string;
  readonly tokens?: readonly InlineTokenLeftContext[];
}

/**
 * The rendered character at the end of `token` — the last character a reader
 * sees in it, not the last character of the markup that produced it.
 *
 * Descends to the deepest final child before reading, because a nesting token's
 * own `text` is still markup one level down: `**a *b***` carries `text` of
 * `"a *b*"`, whose last character is a delimiter that no reader ever sees.
 *
 * Cost is the nesting depth, not the length of the text — it follows the last
 * child only, never a sibling. Depth is bounded well below anything that matters
 * here: marked's own lexer exhausts the stack building a tree deeper than about
 * 2000, so it fails on such input before this walk is ever handed one.
 */
function lastRenderedChar(token: InlineTokenLeftContext): string {
  const children = token.tokens;
  const lastChild = children?.[children.length - 1];
  if (lastChild) return lastRenderedChar(lastChild);

  /* eslint-disable-next-line nexus/blank-defeats-nullish-fallback -- marked derives
     `text` from `raw` (an escape `\*` has text `*`, a codespan drops its backticks),
     so `text` is what renders. A blank one is a token that renders no text — an
     empty link `[](url)` or an alt-less image lexes to `text: ""` beside a
     non-blank `raw` — and falling through to `raw` would return a source
     delimiter like `)` that no reader ever sees. */
  return lastCodePoint(token.text ?? token.raw ?? "");
}

/**
 * The character immediately before marked's inline cursor, read off the tokens
 * it has already emitted for this paragraph — the empty string at the start of
 * one, where a mention is admitted.
 *
 * marked calls an inline extension's tokenizer as `tokenizer(src, tokens)`
 * before any built-in rule, and `src` begins exactly where the last token's
 * `raw` ended. That last token is the only left context a tokenizer gets, and
 * its rendered final character is what {@link isMentionTriggerBoundary} needs to
 * tell `@support` from the `@` inside `gilvan@nexus`.
 *
 * Rendered rather than raw, so that this agrees with the suggestion matcher,
 * which reads the document and therefore sees `x` in front of the trigger in
 * `**x**@nexus`. Reading the markup instead would offer `*`, and the same text
 * would resolve one way as it is typed and the other way as it is loaded — the
 * divergence this boundary exists to remove.
 */
export function precedingCharFromTokens(tokens?: readonly InlineTokenLeftContext[]): string {
  const last = tokens?.[tokens.length - 1];
  return last ? lastRenderedChar(last) : "";
}

// Placeholder mentions: [placeholder text]
// - excludes | so a pipe stays a delimiter: [option1|option2] is text, not a mention
// - excludes \ so an ESCAPED bracket is not a mention: `\[d\]` matched as `[d\]`
//   before, carrying the escape into the captured text
// - not followed by ( or [ to avoid matching markdown links
export const PLACEHOLDER_MENTION_PATTERN = /\[([^\\\]|]+)\](?![([])/;
export const PLACEHOLDER_MENTION_PATTERN_GLOBAL = /\[([^\\\]|]+)\](?![([])/g;

// Collection mentions: %%collectionName%%
export const COLLECTION_MENTION_PATTERN = /%%([^%]+)%%/;
export const COLLECTION_MENTION_PATTERN_GLOBAL = /%%([^%]+)%%/g;

/**
 * All mention patterns that should NEVER be escaped during string replacement.
 *
 * Read by `protectByPatterns` in the chat suggestion card's `unescapeMarkdown`,
 * which swaps each match for a token before markdown escapes are stripped and
 * swaps it back afterwards, so a mention cannot be corrupted on the way through.
 *
 * ⚠️ CRITICAL: When adding a new mention type, add the GLOBAL pattern here
 * so it is protected on that path automatically.
 */
export const PROTECTED_MENTION_PATTERNS: RegExp[] = [
  SKILL_MENTION_PATTERN_GLOBAL, // @skillName
  VARIABLE_MENTION_PATTERN_GLOBAL, // {{variableName}}
  PLACEHOLDER_MENTION_PATTERN_GLOBAL, // [placeholder]
  COLLECTION_MENTION_PATTERN_GLOBAL // %%collection%%
];

// =============================================================================
// Ordered List Counters (shared between serializer and conversion utils)
// =============================================================================

/**
 * Converts a number to a Roman numeral string (uppercase).
 *
 * @param num - Positive integer to convert
 * @returns Uppercase Roman numeral (e.g., 1 → "I", 4 → "IV", 42 → "XLII")
 *
 * @example
 * toRomanNumeral(1)   // "I"
 * toRomanNumeral(4)   // "IV"
 * toRomanNumeral(42)  // "XLII"
 */
export function toRomanNumeral(num: number): string {
  if (num <= 0) num = 1; // Clamp to minimum valid value
  const romanNumerals: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"]
  ];

  let result = "";
  for (const [value, symbol] of romanNumerals) {
    while (num >= value) {
      result += symbol;
      num -= value;
    }
  }
  return result;
}

/**
 * Creates the ordered-list counter map with a configurable delimiter.
 *
 * Each counter function maps a 1-based index to its formatted prefix string.
 *
 * | Key | Example output (delimiter = ".") | Example output (delimiter = ")") |
 * |-----|----------------------------------|----------------------------------|
 * | "1" | `1.`                             | `1)`                             |
 * | "a" | `a.`                             | `a)`                             |
 * | "A" | `A.`                             | `A)`                             |
 * | "i" | `i.`                             | `i)`                             |
 * | "I" | `I.`                             | `I)`                             |
 *
 * @param delimiter - Character appended after the counter value (e.g., "." or ")")
 * @returns Record mapping list-type attribute to counter function
 *
 * @example
 * // Markdown serializer style: "1.", "a.", "i."
 * const counters = createOrderedListCounters(".");
 * counters["1"](3)  // "3."
 * counters["i"](4)  // "iv."
 *
 * @example
 * // AI-text conversion style: "1)", "a)", "i)"
 * const counters = createOrderedListCounters(")");
 * counters["1"](3)  // "3)"
 * counters["i"](4)  // "iv)"
 */
export function createOrderedListCounters(
  delimiter: string
): Record<string, (n: number) => string> {
  // Alphabet counter with wraparound: a-z, aa-az, ba-bz, ...
  const alphabetLower = (n: number): string => {
    if (n <= 26) return String.fromCharCode(96 + n);
    // aa, ab, ..., az, ba, bb, ...
    const first = Math.floor((n - 1) / 26);
    const second = ((n - 1) % 26) + 1;
    return String.fromCharCode(96 + first) + String.fromCharCode(96 + second);
  };

  const alphabetUpper = (n: number): string => {
    if (n <= 26) return String.fromCharCode(64 + n);
    const first = Math.floor((n - 1) / 26);
    const second = ((n - 1) % 26) + 1;
    return String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
  };

  return {
    "1": (n) => `${n}${delimiter}`,
    a: (n) => `${alphabetLower(n)}${delimiter}`,
    A: (n) => `${alphabetUpper(n)}${delimiter}`,
    i: (n) => toRomanNumeral(n).toLowerCase() + delimiter,
    I: (n) => toRomanNumeral(n) + delimiter
  };
}
